<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useBreakpoints } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import type { IBreadcrumbItem } from '@/types/BreadCrumb'

const props = defineProps({
	breadcrumbs: {
		type: Array as PropType<IBreadcrumbItem[]>,
		required: true,
		default: () => [],
	},
	isLastItemSelf: {
		type: Boolean,
		default: false,
	},
})

const { isSmall } = useBreakpoints()

const formattedBreadcrumbs = computed(() => {
	if (isSmall.value) {
		if (props.breadcrumbs.length < 4) {
			return props.breadcrumbs
		}
		const hiddenItems = props.breadcrumbs.slice(1, -2)
		return [
			props.breadcrumbs[0],
			...hiddenItems.map((item, index) => ({
				...item,
				isEllipsis: true,
				isEllipsisFirst: index === 0,
			})),
			...getLastNItems(2),
		]
	}

	return props.breadcrumbs
})

function getLastNItems(n: number) {
	return props.breadcrumbs.slice(-n)
}
</script>

<template>
	<nav
		class="flex max-w-full flex-nowrap items-center overflow-x-auto whitespace-nowrap"
		aria-label="Breadcrumb"
	>
		<template
			v-for="(item, index) in formattedBreadcrumbs"
			:key="item.categoryId"
		>
			<span
				v-if="isLastItemSelf && index === formattedBreadcrumbs.length - 1"
				class="typography-s2-reg text-n-300"
				aria-current="page"
			>
				{{ item.displayName }}
			</span>
			<template v-else>
				<a
					:href="item.link"
					class="not-default-style typography-s2-reg text-n-950 hover:text-o-500 hover:underline"
					:class="{ hidden: item.isEllipsis }"
					:aria-label="`Go to ${item.displayName}`"
				>
					{{ item.displayName }}
				</a>
			</template>
			<span v-if="item.isEllipsisFirst">...</span>
			<BaseIcon
				v-if="
					index < formattedBreadcrumbs.length - 1 &&
					(!item.isEllipsis || item.isEllipsisFirst)
				"
				name="chevron_thin"
				class="mx-8px h-12px w-12px flex-shrink-0 text-n-300"
				aria-hidden="true"
			/>
		</template>
	</nav>
</template>
